import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Mic, Send, Volume2, VolumeX } from "lucide-react";
import { api, apiError } from "@/lib/api";

const GREETING = "Welcome to EC DREAM. I'm Zara. How can I help you today?";

function newSession() {
  return "zara_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const ZaraWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const sessionRef = useRef(newSession());
  const recogRef = useRef(null);
  const scrollRef = useRef(null);
  const voiceSupported = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const speak = (text) => {
    if (!voiceOn || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1; u.pitch = 1.05; u.lang = "en-US";
    window.speechSynthesis.speak(u);
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: GREETING }]);
      speak(GREETING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const stopSpeaking = () => { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/zara/chat", { session_id: sessionRef.current, message: q });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      speak(data.reply);
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || "Zara is unavailable right now.";
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    } finally { setBusy(false); }
  };

  const toggleMic = () => {
    if (!voiceSupported) return;
    if (listening) { recogRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = "en-US"; r.interimResults = false; r.maxAlternatives = 1;
    stopSpeaking();
    r.onstart = () => setListening(true);
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    r.onresult = (e) => {
      const said = e.results[0][0].transcript;
      setInput(said);
      send(said);
    };
    recogRef.current = r;
    r.start();
  };

  const closeAll = () => { stopSpeaking(); recogRef.current?.stop?.(); setOpen(false); };

  return (
    <>
      {!open && (
        <button
          data-testid="zara-open-button"
          onClick={() => setOpen(true)}
          aria-label="Open Zara AI support"
          className="fixed bottom-5 end-5 z-50 flex items-center gap-2 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 text-black font-semibold shadow-lg shadow-orange-500/30 px-4 py-3 hover:scale-105 transition-transform"
        >
          <Sparkles className="w-5 h-5" />
          <span className="hidden sm:inline">Ask Zara</span>
        </button>
      )}

      {open && (
        <div
          data-testid="zara-panel"
          className="fixed bottom-4 end-4 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl overflow-hidden border border-orange-500/30 bg-pure shadow-2xl shadow-black/20"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-orange-600/30 to-yellow-500/20 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-yellow-400 flex items-center justify-center text-black">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-none">Zara</p>
                <p className="text-[11px] text-orange-300/80 mt-0.5">AI Support Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="zara-voice-toggle" onClick={() => { setVoiceOn((v) => { if (v) stopSpeaking(); return !v; }); }}
                aria-label="Toggle voice" className="text-gray-300 hover:text-white p-1.5">
                {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button data-testid="zara-close-button" onClick={closeAll} aria-label="Close Zara" className="text-gray-300 hover:text-white p-1.5">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="zara-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-orange-600 text-pure rounded-br-sm"
                    : "bg-white/8 text-gray-100 rounded-bl-sm border border-white/10"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/8 border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/10 bg-white/5">
            {listening && <p className="text-[11px] text-orange-300 mb-2 animate-pulse text-center">Listening… speak now</p>}
            <div className="flex items-center gap-2">
              {voiceSupported && (
                <button data-testid="zara-mic-button" onClick={toggleMic} aria-label="Voice input"
                  className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    listening ? "bg-red-600 text-pure animate-pulse" : "bg-white/10 text-orange-300 hover:bg-white/20"}`}>
                  <Mic className="w-4 h-4" />
                </button>
              )}
              <input
                data-testid="zara-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask Zara anything…"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-orange-500/50"
              />
              <button data-testid="zara-send-button" onClick={() => send()} disabled={busy || !input.trim()}
                aria-label="Send" className="shrink-0 w-10 h-10 rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-pure flex items-center justify-center">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
